import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import {
  DateRangePopupComponent,
  DateRangeService,
} from '@progress/kendo-angular-dateinputs';
import { BaseComponent } from '@stockaid/core';
import { startOfDay } from 'date-fns';
import { fromEvent, Subscription } from 'rxjs';
import { filter, tap, debounceTime } from 'rxjs/operators';
import { DateRangePeriod } from 'src/app/core/models/user';
import { convertPeriodToDateRange } from '@stockaid/utils';

interface IDateRange {
  type?: DateRangePeriod;
  start: Date | null;
  end: Date | null;
}

interface IDateRangePeriod {
  label: string;
  value: DateRangePeriod;
}

interface DateRangeClickEventListener extends MouseEvent {
  _popupWasOpen?: boolean;
  _isOutside?: boolean;
}

@Component({
  selector: 'app-date-range-selection',
  templateUrl: './date-range-selection.component.html',
  styleUrls: ['./date-range-selection.component.scss'],
})
export class DateRangeSelectionComponent
  extends BaseComponent
  implements AfterViewInit, OnDestroy
{
  @Input() value?: IDateRange = {
    type: DateRangePeriod.CUSTOM,
    start: null,
    end: null,
  };

  @Input() min?: Date;
  @Input() max?: Date;

  public disabledDates = (date: Date): boolean => {
    return (this.min && date < this.min) || (this.max && date > this.max);
  };

  private snapshotRange: IDateRange | null = null;
  private documentClickSubscription: Subscription;
  private isPreventClose = false;
  private isPreventOpen = false;
  private isUpdateValue = false;

  @Output() valueChange: EventEmitter<IDateRange> =
    new EventEmitter<IDateRange>();

  @ViewChild(DateRangePopupComponent) popup: DateRangePopupComponent;
  @ViewChild('popupWrapper') popupWrapper: ElementRef;
  @ViewChild('daterange', { read: DateRangeService })
  public service: DateRangeService;

  @ViewChild('daterange', { read: ElementRef })
  public dateRangeElement: ElementRef;

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (this.popup?.show) {
      this.popup.cancelPopup();
    }
  }

  readonly DATE_FORMAT = 'MM/dd/yyyy';
  DEFAULT_PERIODS: IDateRangePeriod[] = [
    {
      label: 'Yesterday',
      value: DateRangePeriod.YESTERDAY
    },
    {
      label: 'Last 7 days',
      value: DateRangePeriod.LAST_7_DAYS,
    },
    {
      label: 'Last 14 days',
      value: DateRangePeriod.LAST_14_DAYS,
    },
    {
      label: 'Last 30 days',
      value: DateRangePeriod.LAST_30_DAYS,
    },
    {
      label: 'Last 90 days',
      value: DateRangePeriod.LAST_90_DAYS,
    },
  ];

  public selectPeriod(period: DateRangePeriod) {
    this.value.type = period;
    const { start, end } = convertPeriodToDateRange(period, this.max);

    this.service.setRange({
      start: startOfDay(start),
      end: startOfDay(end),
    });
  }

  onApply(): void {
    this.valueChange.emit(this.value);

    // Mark to revert value or not?
    this.isUpdateValue = true;
    // Manually close popup
    this.popup.cancelPopup();
    this.popup.toggle(false);
  }

  onCancel(): void {
    // Manually close popup
    this.popup.cancelPopup();
    this.popup.toggle(false);
  }

  ngAfterViewInit(): void {
    // Setup click outside listener when popup opens
    this.setupDocumentClickListener();

    this.popup.open
      .pipe(
        this.autoCleanUp(),
        tap((e) => {

          // Prevent user can be spam the inputs (Like add debounce)
          if(this.isPreventOpen){
            e.preventDefault();
            this.isPreventOpen = false;
            return;
          }

          // Update the snapshot range to prepare the data to revert
          this.snapshotRange = { ...this.value };
        })
      )
      .subscribe();

      // When you trigger cancelPopup(), this method fires the "Cancel" event
     this.popup.cancel
      .pipe(
        this.autoCleanUp(),
        tap(() => {
          if (!this.isUpdateValue) {
            // Reset
            // Revert the previous value if user not apply
            this.service.setRange({ start: this.snapshotRange.start, end: this.snapshotRange.end });
            this.value.type = this.snapshotRange.type;
          } else {
             setTimeout(() => {
              this.isUpdateValue = false;
            }, 10)
          }

        })).subscribe()

    // NOTICE: Kendo UI Date Range have default listener to close the popup if you click outside the MultiViewCalendar
    // So I have listen on "mousedown" to double check if click inside the PopupWrapper
    // I will prevent close popup action
    this.popup.close
      .pipe(
        this.autoCleanUp(),
        tap((e) => {
          if (this.isPreventClose) {
            e.preventDefault();
            this.isPreventClose = false;
            return;
          }

          // The Guard safety for prevent other behavior can be open again popup immediately
          this.isPreventOpen = true;
          setTimeout(() => {
            this.isPreventOpen = false;
          }, 10)
        })
      )
      .subscribe();

    this.service.range$
      .pipe(
        this.autoCleanUp(),
        debounceTime(10),
        tap((range) => {
          if(!range?.start || !range.end){
            return;
          }

          if (this.value.type === DateRangePeriod.CUSTOM) {
            return;
          }

          const periodRangeRange = convertPeriodToDateRange(this.value.type, this.max);
          const start = startOfDay(periodRangeRange.start);
          const end = startOfDay(periodRangeRange.end);

          const isDifferentRange = range.start?.getTime() !== start?.getTime() || range?.end.getTime() !== end?.getTime()

          if(isDifferentRange){
            this.value.type = DateRangePeriod.CUSTOM
          }
        })
      )
      .subscribe();
  }

  private setupDocumentClickListener(): void {
    // Use MOUSEDOWN on WINDOW with capture phase
    this.documentClickSubscription = fromEvent<DateRangeClickEventListener>(
      window,
      'mousedown',
      { capture: true }
    )
      .pipe(
        this.autoCleanUp(),
        // Capture both event and popup state at the moment of click
        tap((event) => {
          const popupWasOpen = this.popup?.show;
          const target = event.target as Node;
          const isOutside = !this.contains(target);

          // Store state in the event object for later use
          event._popupWasOpen = popupWasOpen;
          event._isOutside = isOutside;

          this.isPreventClose = popupWasOpen && !isOutside;
        }),
        // Filter based on captured state
        filter((event) => event._popupWasOpen === true),
        filter((event) => event._isOutside === true),
        // Small delay to prevent rapid-fire closes
        debounceTime(10),
      )
      .subscribe(() => {
        this.popup.cancelPopup();
      });
  }

  private contains(target: Node): boolean {
    const popupElement = this.popupWrapper?.nativeElement;
    const animationContainer = document.querySelector('.k-animation-container');
    const dateRangeEl = this.dateRangeElement?.nativeElement;

    return (
      (popupElement && popupElement.contains(target)) ||
      (animationContainer && animationContainer.contains(target)) ||
      (dateRangeEl && dateRangeEl.contains(target))
    );
  }

  ngOnDestroy(): void {
    if (this.documentClickSubscription) {
      this.documentClickSubscription.unsubscribe();
    }
    super.ngOnDestroy();
  }
}
